<!-- eslint-disable no-unused-vars -->
<template>
  <div class="chart-wrapper">
    <div class="chart-scroll">
      <div ref="chartContainer" class="chart-inner"></div>
    </div>
    <!-- 預留：績效資訊區塊（幾日進場、目前報酬率等） -->
    <div class="perf-info">
      <!-- 未來填入內容 -->
    </div>
  </div>
</template>

<script>
import { ref, onMounted, reactive, watch, onUnmounted } from 'vue';
import { useRoute } from 'vue-router';
import * as echarts from 'echarts';
import { getData } from '@/api/chart';
import { useStockStore } from '@/stores/stockStore';

export default {
  name: 'ChartView',
  setup() {
    const chartContainer = ref(null);
    const chartData = reactive({
      rawData: {
        stock_code: '',
        stock_name: '',
        items: [],
        volume: [],
        lr: [],
        ma22: [],
        entryScore: [],
        exitScore: [],
      },
    });

    const route = useRoute();
    const stockStore = useStockStore();
    let fetchInterval = null;

    async function fetchData() {
      try {
        let id = route.params.code;
        if (!id) id = '2027';

        const response = await getData(id);
        chartData.rawData = response.data;
        stockStore.setStock(response.data.stock_code, response.data.stock_name);
        initializeChart();
      } catch (error) {
        console.error('Error fetching chart data:', error);
      }
    }

    let chartInstance = null;
    let resizeObserver = null;

    function initializeChart() {
      if (chartContainer.value) {
        if (chartInstance) {
          chartInstance.dispose();
        }
        chartInstance = echarts.init(chartContainer.value);

        const rawData = chartData.rawData.items;
        const categoryData = rawData.map(item => item.time);
        const values = rawData.map(item => [item.open, item.close, item.lowest, item.highest]);
        const line1data = chartData.rawData.lr55;
        const line2data = chartData.rawData.lr21;
        const volumeData = chartData.rawData.volume;
        const entryScoreData = chartData.rawData.entryScore;
        const exitScoreData = chartData.rawData.exitScore;
        
        const option = {
          title: { show: false },
          tooltip: {
            trigger: 'axis',
            confine: true,
            axisPointer: {
              type: 'cross',
            },
            formatter(params) {
              let entry = null, exit = null;
              let html = `<div style="font-size:12px">`;
              for (const p of params) {
                if (p.seriesName === '日K' && Array.isArray(p.value)) {
                  const open = p.value[0] != null ? Number(p.value[0]).toFixed(2) : '-';
                  const close = p.value[1] != null ? Number(p.value[1]).toFixed(2) : '-';
                  html += `<div>開：${open}</div><div>收：${close}</div>`;
                } else {
                  if (p.seriesName === '進場訊號') entry = p.value;
                  if (p.seriesName === '出場訊號') exit = p.value;
                }
              }
              if (entry !== null || exit !== null) {
                const entryVal = Math.abs(entry ?? 0).toFixed(2);
                const exitVal = Math.abs(exit ?? 0).toFixed(2);
                const signal = (Math.abs(entry ?? 0) + Math.abs(exit ?? 0)).toFixed(2);
                html += `<div style="margin-top:4px;border-top:1px solid #ccc;padding-top:4px">`;
                if (entry !== null) html += `<div>進：${entryVal}</div>`;
                if (exit !== null) html += `<div>出：${exitVal}</div>`;
                html += `<div><b>信：${signal}</b></div>`;
                html += `</div>`;
              }
              html += `</div>`;
              return html;
            },
          },
          axisPointer: {
            link: [{ xAxisIndex: 'all' }],
          },
          legend: {
            data: ['日K', '控盤線', '走勢線', '成交量', '進場訊號', '出場訊號'],
            top: 30,
            show: false,
          },
          dataZoom: [
            {
              type: 'inside',
              xAxisIndex: [0, 1, 2],
              start: 0,
              end: 100,
            },
          ],
          grid: [
            {
              left: '2%',
              right: '8%',
              top: '2%',
              height: '48%',
            },
            {
              left: '2%',
              right: '8%',
              top: '54%',
              height: '7%',
            },
            {
              left: '2%',
              right: '8%',
              top: '64%',
              height: '12%',
            },
          ],
          xAxis: [
            {
              type: 'category',
              data: categoryData,
              boundaryGap: false,
              axisLine: { onZero: false },
            },
            {
              type: 'category',
              data: categoryData,
              gridIndex: 1,
              axisTick: { show: false },
              axisLabel: { show: false },
              axisLine: { show: false },
            },
            {
              type: 'category',
              data: categoryData,
              gridIndex: 2, // 第三張圖表的 x 軸
              axisTick: { show: false },
              axisLabel: { show: true },
              axisLine: { show: true },
            },
          ],
          yAxis: [
            {
              scale: true,
              position: 'right',
            },
            {
              scale: true,
              gridIndex: 1,
              position: 'right',
              splitNumber: 3,
              splitLine: { show: false },
              axisLabel: { formatter: (v) => (v / 1000).toFixed(0) + 'K' },
            },
            {
              scale: true,
              gridIndex: 2,
              position: 'right',
              min: -1,
              max: 0,
              splitNumber: 4,
              splitLine: { show: true },
              axisLabel: { formatter: (v) => v.toFixed(1) },
            },
          ],
          series: [
            {
              name: '日K',
              type: 'candlestick',
              data: values,
              itemStyle: {
                color: '#ec0000',
                color0: '#00da3c',
                borderColor: '#8A0000',
                borderColor0: '#008F28',
              },
            },
            {
              name: '控盤線',
              type: 'line',
              data: line1data,
              smooth: false,
              showSymbol: false,
              lineStyle: { opacity: 0.5 },
            },
            {
              name: '走勢線',
              type: 'line',
              data: line2data,
              smooth: false,
              showSymbol: false,
              lineStyle: { opacity: 0.5 },
            },
            {
              name: '成交量',
              type: 'bar',
              data: volumeData,
              xAxisIndex: 1,
              yAxisIndex: 1,
              itemStyle: {
                color: '#7fbe9e',
              },
            },
            {
              name: '進場訊號',
              type: 'line',
              data: entryScoreData,
              xAxisIndex: 2,
              yAxisIndex: 2,
              connectNulls: false,
              showSymbol: false,
              lineStyle: { color: '#e84545' },
              itemStyle: { color: '#e84545' },
            },
            {
              name: '出場訊號',
              type: 'line',
              data: exitScoreData,
              xAxisIndex: 2,
              yAxisIndex: 2,
              connectNulls: false,
              showSymbol: false,
              lineStyle: { color: '#2196f3' },
              itemStyle: { color: '#2196f3' },
            },
          ],
        };

        chartInstance.setOption(option);

        // 手機版寬度 200%，自動捲到最右側（最新資料）
        const scrollEl = chartContainer.value.parentElement;
        if (scrollEl) {
          scrollEl.scrollLeft = scrollEl.scrollWidth;
        }

        // 監聽容器大小變化自動 resize
        if (!resizeObserver) {
          resizeObserver = new ResizeObserver(() => {
            chartInstance && chartInstance.resize();
          });
          resizeObserver.observe(chartContainer.value);
        }
      }
    }

    onMounted(() => {
      fetchData();

      // 監聽路由參數變化
      watch(
        () => route.params.code,
        (newCode) => {
          fetchData();
          clearInterval(fetchInterval); // 清除之前的定時器
          if (newCode === 'min1') {
            fetchInterval = setInterval(fetchData, 60000); // 每分鐘執行一次
          } else if (newCode === 'min3') {
            fetchInterval = setInterval(fetchData, 60000 * 3); // 每3分鐘執行一次
          } else if (newCode === 'min5') {
            fetchInterval = setInterval(fetchData, 60000 * 5); // 每5分鐘執行一次
          }
            // } else {
          //   fetchData(); // 非定時模式時立即執行一次
          // }
        },
        { immediate: true } // 立即執行一次監聽器
      );
    });

    onUnmounted(() => {
      clearInterval(fetchInterval);
      if (resizeObserver) resizeObserver.disconnect();
      if (chartInstance) chartInstance.dispose();
    });

    return {
      chartContainer,
    };
  },
};
</script>

<style scoped>
.chart-wrapper {
  width: 100%;
  height: 100%;
  display: flex;
  flex-direction: column;
}

.chart-scroll {
  flex: 1;
  overflow-x: auto;
  overflow-y: hidden;
  -webkit-overflow-scrolling: touch;
  min-height: 0;
}

.chart-inner {
  height: 100%;
  min-height: 400px;
  width: 100%;
}

/* 底部績效資訊預留區，固定高度 */
.perf-info {
  flex-shrink: 0;
  height: 80px;
  padding: 6px 10px;
  border-top: 1px solid #dee2e6;
  font-size: 12px;
  color: #555;
  background: #fafafa;
}

@media (max-width: 767px) {
  .chart-inner {
    width: 200%;
  }

  .perf-info {
    height: 72px;
  }
}
</style>
